import React from 'react';
import { Send, CheckCircle } from 'lucide-react';

const DemoSubmission = () => {
  return (
    <section id="demo" className="relative py-24 bg-black">
      {/* Background effect */}
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,_var(--tw-gradient-stops))] from-green-900/10 via-black to-black"></div>

      <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Title */}
        <div className="text-center mb-12">
          <h2 className="text-4xl sm:text-5xl md:text-6xl font-bold text-white mb-4">
            SUBMIT YOUR
            <span className="block text-green-400 mt-2 drop-shadow-[0_0_20px_rgba(0,255,136,0.8)]">
              DEMO
            </span>
          </h2>
          <p className="text-2xl text-white font-semibold mt-6">
            NUMERO UNO SOUNDS
          </p>
          <div className="w-24 h-1 bg-green-500 mx-auto mt-6 shadow-[0_0_10px_rgba(0,255,136,0.8)]"></div>
        </div>

        {/* Label Info */}
        <div className="bg-zinc-900/50 backdrop-blur-sm border border-green-500/20 rounded-2xl p-8 mb-8">
          <p className="text-gray-300 text-lg leading-relaxed mb-6">
            Numero Uno Sounds is a growing drum & bass label focused on powerful, energetic music with identity.
          </p>
          <p className="text-gray-300 leading-relaxed mb-6">
            We are looking for artists who bring something unique — whether that's heavy Jump-Up, Jungle, Neurofunk, or dark underground drum & bass.
          </p>
          <p className="text-gray-300 leading-relaxed mb-6 italic">
            No commercial. No frog jump-up. We want heavy, dark, impactful music.
          </p>
          <p className="text-white font-semibold text-lg mb-4">Our goal is simple:</p>
          <p className="text-green-400 text-lg mb-8">
            Quality music. Strong identity. Real progression.
          </p>

          {/* What We Look For */}
          <h3 className="text-2xl font-bold text-white mb-4 flex items-center gap-2">
            <CheckCircle className="text-green-400" size={24} />
            WHAT WE LOOK FOR
          </h3>
          <ul className="space-y-2 mb-8 text-gray-300">
            <li className="flex items-start gap-2">
              <span className="text-green-400 mt-1">•</span>
              <span>Clean structure and arrangement</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-green-400 mt-1">•</span>
              <span>Strong energy and impact</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-green-400 mt-1">•</span>
              <span>Original ideas and personality</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-green-400 mt-1">•</span>
              <span>Mixdown clarity (or clear potential)</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-green-400 mt-1">•</span>
              <span>Artists who are serious about improving</span>
            </li>
          </ul>

          <p className="text-white font-semibold text-lg mb-4">
            You don't have to be perfect.
          </p>
          <p className="text-gray-300 leading-relaxed mb-8">
            If you've got the right attitude, passion, and willingness to grow, we're open to working with developing producers as well as established artists.
          </p>

          {/* What Makes Us Different */}
          <h3 className="text-2xl font-bold text-white mb-4">WHAT MAKES US DIFFERENT</h3>
          <ul className="space-y-2 mb-8 text-gray-300">
            <li className="flex items-start gap-2">
              <span className="text-green-400 mt-1">•</span>
              <span>Friendly, supportive label environment</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-green-400 mt-1">•</span>
              <span>Honest feedback — no ego, no arrogance</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-green-400 mt-1">•</span>
              <span>Guidance on structure, direction, and progression if needed</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-green-400 mt-1">•</span>
              <span>Opportunity to grow with the label long-term</span>
            </li>
          </ul>

          <p className="text-gray-300 italic mb-8">
            We believe great artists are built through passion and consistency — not just experience.
          </p>

          {/* About Lost in Bass */}
          <h3 className="text-2xl font-bold text-white mb-4">ABOUT LOST IN BASS</h3>
          <p className="text-gray-300 leading-relaxed mb-4">
            Lost in Bass is the lead artist of Numero Uno Sounds, known for heavy, emotional drum & bass that connects with crowds while maintaining musical depth and identity.
          </p>
          <p className="text-gray-300 leading-relaxed mb-6">
            The label reflects the same philosophy: <span className="text-green-400 font-semibold">Music that hits hard but still means something.</span>
          </p>
          <p className="text-white text-lg font-semibold">
            If you feel your sound fits the energy of Numero Uno Sounds, we want to hear from you.
          </p>
        </div>

        {/* Terms & Conditions */}
        <div className="bg-zinc-900/70 backdrop-blur-sm border border-red-500/30 rounded-2xl p-8 mb-8">
          <h3 className="text-3xl font-bold text-white mb-6 text-center">
            TERMS & CONDITIONS
          </h3>
          <div className="space-y-4 text-gray-300 text-sm leading-relaxed">
            <div className="flex items-start gap-3">
              <span className="text-red-400 font-bold flex-shrink-0">1.</span>
              <p>Your track must be 100% your own work or you must have full written permission to use any vocals, samples or copyrighted material.</p>
            </div>
            <div className="flex items-start gap-3">
              <span className="text-red-400 font-bold flex-shrink-0">2.</span>
              <p>We <strong className="text-white">DO NOT</strong> accept tracks containing uncleared copyrighted material or recognisable commercial samples.</p>
            </div>
            <div className="flex items-start gap-3">
              <span className="text-red-400 font-bold flex-shrink-0">3.</span>
              <p>If your track has already been released or distributed through any other distributor or label we <strong className="text-white">CANNOT</strong> release it on Numero Uno Sounds.</p>
            </div>
            <div className="flex items-start gap-3">
              <span className="text-red-400 font-bold flex-shrink-0">4.</span>
              <p>If you have uploaded your track to DistroKid, Ditto, TuneCore, CD Baby, Amuse, UnitedMasters or any other distributor <strong className="text-white">DO NOT</strong> submit it to Numero Uno Sounds until it has been completely removed from every platform. Duplicate releases will be rejected immediately.</p>
            </div>
            <div className="flex items-start gap-3">
              <span className="text-red-400 font-bold flex-shrink-0">5.</span>
              <p>Your track must not be under an exclusive agreement with another label, publisher or distributor.</p>
            </div>
            <div className="flex items-start gap-3">
              <span className="text-red-400 font-bold flex-shrink-0">6.</span>
              <p>You confirm that you own all rights to the music you submit.</p>
            </div>
            <div className="flex items-start gap-3">
              <span className="text-red-400 font-bold flex-shrink-0">7.</span>
              <p>Submitted tracks must be supplied as a high quality <strong className="text-white">WAV file</strong>. We do not accept MP3s for mastering or release.</p>
            </div>
            <div className="flex items-start gap-3">
              <span className="text-red-400 font-bold flex-shrink-0">8.</span>
              <p>By submitting your music you confirm that all information you provide is accurate. Any false information may result in your release being cancelled.</p>
            </div>
            <div className="flex items-start gap-3">
              <span className="text-red-400 font-bold flex-shrink-0">9.</span>
              <p>Numero Uno Sounds reserves the right to reject any submission without giving a reason.</p>
            </div>
            <div className="flex items-start gap-3">
              <span className="text-red-400 font-bold flex-shrink-0">10.</span>
              <p>If any copyright claim or legal issue arises after release, the artist accepts full responsibility and agrees to indemnify Numero Uno Sounds against any claims, costs or damages.</p>
            </div>
          </div>
          <div className="mt-6 p-4 bg-red-500/10 border border-red-500/30 rounded-lg">
            <p className="text-red-400 font-semibold text-center">
              By submitting your demo, you agree to these terms and conditions.
            </p>
          </div>
        </div>

        {/* Send Your Demos */}
        <div className="bg-gradient-to-br from-green-500/10 to-purple-500/10 border border-green-500/30 rounded-2xl p-8 text-center">
          <h3 className="text-3xl font-bold text-white mb-4">SEND YOUR DEMOS</h3>
          <div className="text-gray-300 mb-6 space-y-2">
            <p>
              <strong>Label Email:</strong> <a href="mailto:numerounosounds@proton.me" className="text-green-400 hover:text-green-300 transition-colors">numerounosounds@proton.me</a>
            </p>
            <p>
              <strong>Lost in Bass Direct:</strong> <a href="mailto:lostinbass.official@outlook.com" className="text-green-400 hover:text-green-300 transition-colors">lostinbass.official@outlook.com</a>
            </p>
          </div>
          
          <div className="text-left max-w-xl mx-auto mb-6">
            <p className="text-white font-semibold mb-3">Please include:</p>
            <ul className="space-y-2 text-gray-300">
              <li className="flex items-start gap-2">
                <span className="text-green-400 mt-1">•</span>
                <span>A private streaming link or downloadable file</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-green-400 mt-1">•</span>
                <span>Artist name / alias</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-green-400 mt-1">•</span>
                <span>Brief introduction about yourself (optional)</span>
              </li>
            </ul>
          </div>

          <a
            href="mailto:numerounosounds@proton.me?subject=Demo%20Submission"
            className="inline-block px-8 py-4 bg-green-500 hover:bg-green-600 text-black font-bold rounded-lg transition-all duration-300 shadow-[0_0_20px_rgba(0,255,136,0.5)] hover:shadow-[0_0_30px_rgba(0,255,136,0.8)] text-lg"
          >
            <Send className="inline mr-2" size={20} />
            Submit Your Demo
          </a>

          <p className="text-green-400 font-semibold mt-8 text-lg">
            Numero Uno Sounds — built on passion, progression, and heavyweight energy.
          </p>
        </div>
      </div>
    </section>
  );
};

export default DemoSubmission;
